import React from 'react';
import { Box, TextField } from '@mui/material';
import { InsertTokenMenu } from './InsertTokenMenu';
import { useTokenAutocomplete } from './useTokenAutocomplete';

/**
 * An authoring field with an attached insert-token picker — the shared building block wired into every
 * admin surface that authors substitutable text (AI prompt templates, content-block textareas, report
 * prose). It is a thin controlled wrapper: `value`/`onChange` are the field's own value and change
 * handler (so it composes with local-draft or RHF state).
 *
 * There are two ways to insert a canonical `{{group.key}}` token, both routed through
 * {@link useTokenAutocomplete}: the author can type `{{` anywhere in the field — which auto-completes
 * the closing `}}` and opens a searchable picker anchored at the field — or click the always-present
 * "Insert reference" button below it. Both are hidden/disabled when the field is disabled (read-only
 * surfaces). Multiline by default; pass `multiline={false}` to author a single-line input. Persistence
 * stays the caller's job via `onBlur`.
 */
export function TokenTextField(props: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  campaignId: number | undefined;
  /** The order of the step being authored; passed through to the picker's availability filter. */
  uptoStepOrder?: number;
  /** Multiline textarea (default) or a single-line input. */
  multiline?: boolean;
  minRows?: number;
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  sx?: object;
}): JSX.Element {
  const { value, onChange } = props;
  const multiline = props.multiline ?? true;
  const { ref, insert, handleChange, popover } = useTokenAutocomplete({
    value,
    onChange,
    campaignId: props.campaignId,
    uptoStepOrder: props.uptoStepOrder,
    enabled: !props.disabled,
  });

  return (
    <Box sx={props.sx}>
      <TextField
        label={props.label}
        size="small"
        fullWidth={props.fullWidth ?? true}
        multiline={multiline}
        minRows={multiline ? props.minRows ?? 4 : undefined}
        value={value}
        disabled={props.disabled}
        helperText={props.helperText}
        placeholder={props.placeholder}
        onChange={handleChange}
        onBlur={props.onBlur}
        inputRef={ref}
      />
      {popover}
      {!props.disabled && (
        <Box sx={{ mt: 0.5 }}>
          <InsertTokenMenu
            campaignId={props.campaignId}
            uptoStepOrder={props.uptoStepOrder}
            onInsert={(token) => insert(token, value, onChange)}
          />
        </Box>
      )}
    </Box>
  );
}

export default TokenTextField;
